import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Edit, Trash2, Settings, Workflow, Route, Target, 
  Clock, Users, BarChart2, Copy, Save, RotateCcw, AlertTriangle, 
  Search, HelpCircle, CheckCircle, XCircle, Eye, EyeOff, X, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import CreateTicketTypeModal from '../../components/modals/CreateTicketTypeModal';
import CreateRoutingRuleModal from '../../components/modals/CreateRoutingRuleModal';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const TicketTypesManagement = () => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateRoutingRule, setShowCreateRoutingRule] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [activeTab, setActiveTab] = useState('types');
  const [expandedType, setExpandedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [newType, setNewType] = useState({
    name: '',
    description: '',
    icon: 'FileText',
    color: '#3b82f6',
    workflow: {
      name: '',
      steps: []
    },
    routingRules: [],
    slaTargets: {
      response: 1440,
      resolution: 2880
    },
    templates: [],
    customFields: []
  });

  const icons = [
    { value: 'FileText', label: 'File Text', component: FileText },
    { value: 'AlertTriangle', label: 'Alert Triangle', component: AlertTriangle },
    { value: 'Search', label: 'Search', component: Search },
    { value: 'Settings', label: 'Settings', component: Settings },
    { value: 'HelpCircle', label: 'Help Circle', component: HelpCircle },
    { value: 'Workflow', label: 'Workflow', component: Workflow }
  ];

  const colors = [
    { value: '#ef4444', label: 'Red' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#10b981', label: 'Green' },
    { value: '#f97316', label: 'Orange Red' },
    { value: '#06b6d4', label: 'Cyan' },
    { value: '#84cc16', label: 'Lime' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesResponse, templatesResponse, rulesResponse] = await Promise.all([
        MockDataService.getTicketTypes(),
        MockDataService.getWorkflowTemplates(),
        MockDataService.getRoutingRules()
      ]);

      if (typesResponse.success) {
        setTicketTypes(typesResponse.data || []);
      } else {
        setTicketTypes([]);
        setError(typesResponse.message || 'Failed to load ticket types');
      }
      
      if (templatesResponse.success) {
        setWorkflowTemplates(templatesResponse.data || []);
      } else {
        setWorkflowTemplates([]);
      }
      
      if (rulesResponse.success) {
        setRoutingRules(rulesResponse.data || []);
      } else {
        setRoutingRules([]);
      }
    } catch (err) {
      setError('Failed to load ticket types data.');
      setTicketTypes([]);
      setWorkflowTemplates([]);
      setRoutingRules([]);
      console.error('Error loading ticket types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async (typeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await MockDataService.createTicketType(typeData);
      if (response.success) {
        setTicketTypes([...ticketTypes, response.data]);
        setShowCreateForm(false);
        setEditingType(null);
        toast.success('Ticket type created successfully');
        await loadData();
      } else {
        setError(response.message || 'Failed to create ticket type.');
        toast.error(response.message || 'Failed to create ticket type.');
      }
    } catch (err) {
      setError('Failed to create ticket type.');
      toast.error('Failed to create ticket type.');
      console.error('Error creating ticket type:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateType = async (typeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await MockDataService.updateTicketType(editingType.id, typeData);
      if (response.success) {
        setTicketTypes(ticketTypes.map(t => t.id === editingType.id ? response.data : t));
        setShowCreateForm(false);
        setEditingType(null);
        toast.success('Ticket type updated successfully');
        await loadData();
      } else {
        setError(response.message || 'Failed to update ticket type.');
        toast.error(response.message || 'Failed to update ticket type.');
      }
    } catch (err) {
      setError('Failed to update ticket type.');
      toast.error('Failed to update ticket type.');
      console.error('Error updating ticket type:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoutingRule = async (ruleData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await MockDataService.createRoutingRule(ruleData);
      if (response.success) {
        setRoutingRules([...routingRules, response.data]);
        setShowCreateRoutingRule(false);
        setEditingRule(null);
        toast.success('Routing rule created successfully');
        await loadData();
      } else {
        setError(response.message || 'Failed to create routing rule.');
        toast.error(response.message || 'Failed to create routing rule.');
      }
    } catch (err) {
      setError('Failed to create routing rule.');
      toast.error('Failed to create routing rule.');
      console.error('Error creating routing rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoutingRule = async (ruleData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await MockDataService.updateRoutingRule(editingRule.id, ruleData);
      if (response.success) {
        setRoutingRules(routingRules.map(r => r.id === editingRule.id ? response.data : r));
        setShowCreateRoutingRule(false);
        setEditingRule(null);
        toast.success('Routing rule updated successfully');
        await loadData();
      } else {
        setError(response.message || 'Failed to update routing rule.');
        toast.error(response.message || 'Failed to update routing rule.');
      }
    } catch (err) {
      setError('Failed to update routing rule.');
      toast.error('Failed to update routing rule.');
      console.error('Error updating routing rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
    } catch (err) {
      setError('Failed to refresh data.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setEditingType(null);
  };

  const handleCloseRoutingRuleModal = () => {
    setShowCreateRoutingRule(false);
    setEditingRule(null);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowCreateRoutingRule(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this routing rule?')) {
      try {
        const response = await MockDataService.deleteRoutingRule(ruleId);
        if (response.success) {
          setRoutingRules(routingRules.filter(r => r.id !== ruleId));
          toast.success('Routing rule deleted successfully');
          await loadData();
        } else {
          toast.error(response.message || 'Failed to delete routing rule');
        }
      } catch (err) {
        toast.error('Failed to delete routing rule.');
        console.error('Error deleting routing rule:', err);
      }
    }
  };

  const handleDeleteType = async (typeId) => {
    if (window.confirm('Are you sure you want to delete this ticket type?')) {
      try {
        const response = await MockDataService.deleteTicketType(typeId);
        if (response.success) {
          setTicketTypes(ticketTypes.filter(t => t.id !== typeId));
        }
      } catch (err) {
        setError('Failed to delete ticket type.');
        console.error('Error deleting ticket type:', err);
      }
    }
  };

  const handleCopyType = async (type) => {
    try {
      setSaving(true);
      
      // Copy all properties from the original type
      const copiedType = {
        ...type,
        name: `${type.name} (Copy)`,
        id: undefined, // Remove ID so it creates a new type
        createdAt: undefined, // Remove creation timestamp
        updatedAt: undefined, // Remove update timestamp
        status: type.status || 'active',
        // Deep copy nested objects
        workflow: type.workflow ? {
          ...type.workflow,
          steps: type.workflow.steps ? [...type.workflow.steps] : []
        } : { name: '', steps: [] },
        routingRules: type.routingRules ? [...type.routingRules] : [],
        slaTargets: type.slaTargets ? { ...type.slaTargets } : {
          response: 1440,
          resolution: 2880
        },
        templates: type.templates ? [...type.templates] : [],
        customFields: type.customFields ? [...type.customFields] : []
      };
      
      // Create the copied type immediately without opening modal
      const response = await MockDataService.createTicketType(copiedType);
      
      if (response.success) {
        // Add the new copied type to the list
        setTicketTypes([...ticketTypes, response.data]);
        toast.success(`"${type.name}" copied successfully as "${copiedType.name}"`);
      } else {
        toast.error(response.message || 'Failed to copy ticket type');
        setError(response.message || 'Failed to copy ticket type');
      }
    } catch (error) {
      console.error('Error copying ticket type:', error);
      toast.error('Failed to copy ticket type');
      setError('Failed to copy ticket type');
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (iconName) => {
    const icon = icons.find(i => i.value === iconName);
    return icon ? icon.component : FileText;
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} hours`;
    return `${Math.floor(minutes / 1440)} days`;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="text-center text-gray-600">Loading ticket types...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="mr-3" size={28} />
          Ticket Types Management
        </h1>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Create Type
          </Button>
        </div>
      </div>

      <p className="text-gray-700 mb-8">
        Configure ticket types and their workflows to ensure tickets are properly categorized, 
        routed, and processed according to your organization's requirements.
      </p>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading ticket types...</span>
        </div>
      )}

      {/* Tab Navigation */}
      {!loading && (
        <div className="flex space-x-1 mb-6">
          <button
            onClick={() => setActiveTab('types')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'types' 
                ? 'bg-[#006600] text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Ticket Types ({ticketTypes?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('workflows')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'workflows' 
                ? 'bg-[#006600] text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Workflow Templates ({workflowTemplates?.length || 0})
          </button>
        </div>
      )}

      {/* Ticket Types Tab */}
      {!loading && activeTab === 'types' && (
        <div className="space-y-6">
          {/* Ticket Types List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {ticketTypes && ticketTypes.length > 0 ? ticketTypes.map((type) => {
              const IconComponent = getIconComponent(type.icon);
              return (
                <div key={type.id} className="bg-gray-50 rounded-lg p-6 border">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div 
                        className="p-2 rounded-lg mr-3"
                        style={{ backgroundColor: `${type.color}20`, color: type.color }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{type.name}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}
                        className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        title="Toggle Details"
                        aria-label="Toggle details"
                      >
                        {expandedType === type.id ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyType(type);
                        }}
                        disabled={saving}
                        className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Copy Type"
                        aria-label={`Copy ${type.name}`}
                      >
                        {saving ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingType(type);
                          setShowCreateForm(true);
                        }}
                        className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        title="Edit Type"
                        aria-label={`Edit ${type.name}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteType(type.id);
                        }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Delete Type"
                        aria-label={`Delete ${type.name}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <Workflow className="mr-2 text-gray-500" size={14} />
                      <span className="text-gray-600">
                        Workflow: {type.workflow.name}
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      <Route className="mr-2 text-gray-500" size={14} />
                      <span className="text-gray-600">
                        Routing Rules: {type.routingRules?.length || 0}
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      <Clock className="mr-2 text-gray-500" size={14} />
                      <span className="text-gray-600">
                        SLA: {formatMinutes(type.slaTargets?.response || 0)} response, {formatMinutes(type.slaTargets?.resolution || 0)} resolution
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      <FileText className="mr-2 text-gray-500" size={14} />
                      <span className="text-gray-600">
                        Templates: {type.templates?.length || 0}
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      <Settings className="mr-2 text-gray-500" size={14} />
                      <span className="text-gray-600">
                        Custom Fields: {type.customFields?.length || 0}
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      {type.status === 'active' ? (
                        <CheckCircle className="mr-2 text-green-500" size={14} />
                      ) : (
                        <XCircle className="mr-2 text-red-500" size={14} />
                      )}
                      <span className="text-gray-600">Status: {type.status}</span>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedType === type.id && (
                    <div className="mt-6 pt-6 border-t space-y-4">
                      {/* Workflow Steps */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Workflow Steps</h4>
                        <div className="space-y-1">
                          {type.workflow?.steps && type.workflow.steps.length > 0 ? type.workflow.steps.map((step, index) => (
                            <div key={step.id} className="flex items-center text-sm">
                              <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs mr-2">
                                {step.order}
                              </span>
                              <span className="text-gray-600">{step.name}</span>
                            </div>
                          )) : (
                            <div className="text-sm text-gray-500">No workflow steps defined</div>
                          )}
                        </div>
                      </div>

                      {/* Routing Rules */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Routing Rules</h4>
                        <div className="space-y-1">
                          {type.routingRules && type.routingRules.length > 0 ? type.routingRules.map((rule, index) => (
                            <div key={index} className="text-sm text-gray-600">
                              <span className="font-medium">If:</span> {rule.condition} 
                              <span className="font-medium ml-2">Then:</span> {rule.action}
                            </div>
                          )) : (
                            <div className="text-sm text-gray-500">No routing rules defined</div>
                          )}
                        </div>
                      </div>

                      {/* Custom Fields */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Custom Fields</h4>
                        <div className="space-y-1">
                          {type.customFields && type.customFields.length > 0 ? type.customFields.map((field) => (
                            <div key={field.id} className="text-sm text-gray-600">
                              <span className="font-medium">{field.name}</span> ({field.type})
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </div>
                          )) : (
                            <div className="text-sm text-gray-500">No custom fields defined</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="col-span-full text-center py-12">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Ticket Types Found</h3>
                <p className="text-gray-600 mb-4">Get started by creating your first ticket type.</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} className="inline mr-2" />
                  Create Ticket Type
                </button>
              </div>
            )}
          </div>

          {/* Create/Edit Type Modal */}
          <CreateTicketTypeModal
            isOpen={showCreateForm}
            onClose={handleCloseForm}
            ticketType={editingType}
            onSave={(data) => {
              if (editingType) {
                handleUpdateType(data);
              } else {
                handleCreateType(data);
              }
            }}
          />
        </div>
      )}

      {/* Workflow Templates Tab */}
      {activeTab === 'workflows' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflowTemplates && workflowTemplates.length > 0 ? workflowTemplates.map((template) => (
              <div key={template.id} className="bg-gray-50 rounded-lg p-6 border">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                  <button className="text-blue-500 hover:text-blue-700">
                    <Copy size={16} />
                  </button>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-900">Workflow Steps</h4>
                  <div className="space-y-1">
                    {template.steps && template.steps.length > 0 ? template.steps.map((step, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs mr-2">
                          {step.order}
                        </span>
                        <span className="text-gray-600">{step.name}</span>
                      </div>
                    )) : (
                      <div className="text-sm text-gray-500">No steps defined</div>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-12">
                <Workflow size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Workflow Templates Found</h3>
                <p className="text-gray-600 mb-4">Create workflow templates to automate ticket processing.</p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto">
                  <Plus size={16} className="inline mr-2" />
                  Create Workflow Template
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default TicketTypesManagement;